import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Button, EditInput } from '@piplapps/pipl-ui';
import styles from './ChangePassword.module.less';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import ApiService from '../../../Services/ApiService';
import { getText } from '../../../Utils/TranslationUtils';
import AnalyticsCore from '../../../Utils/AnalyticsHandler/AnalyticsCore';

const ChangePassword = (props) => {
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const validate = (values) => {
    const errors = {};
    for (const value in values) {
      if (values[value] === '') {
        errors[value] = gettext('Required');
      }
    }
    if (values.newPassword !== values.confirmPassword) {
      errors.confirmPassword = gettext("Passwords don't match");
    }

    if (passwordError) {
      errors.newPassword = getText(passwordError);
    }

    if (values.oldPassword === values.newPassword) {
      errors.newPassword = gettext('New Password must be different from Old Password');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate,
    onSubmit: (values) => {
      props.changePassword(values);
      setEditMode(false);
    },
  });

  useEffect(() => {
    setSuccess(props.passwordUpdateStatus);
  }, [props.passwordUpdateStatus]);

  async function checkPassword(password) {
    try {
      setPasswordError('evaluating password..');
      await ApiService.checkPassword({ password });
      setPasswordError(null);
      formik.setFieldError('newPassword', null);
    } catch (e) {
      setPasswordError(e.response.data.error);
      formik.setFieldError('newPassword', getText(e.response.data.error));
      const event = AnalyticsCore.buildEvent({
        eventName: AnalyticsEventNames.GOT_ERROR_CHANGING_PASSWORD,
        eventLabel: e.response.data.error,
        tracking: props.tracking,
        user: props.user,
      });
      AnalyticsCore.sendEvent(event);
    }

    return true;
  }
  return (
    <div className={styles.passwordSection}>
      <div className={styles.header} id="password-title">
        {gettext('Password')}
      </div>
      {editMode ? (
        <div className={styles.fieldsEdit}>
          <form onSubmit={formik.handleSubmit} id="change-password-form">
            <EditInput
              label={gettext('Old Password')}
              id="oldPassword"
              name="oldPassword"
              testId="old-password-input"
              type="password"
              onChange={(e) => {
                formik.handleChange(e);
                setSuccess(null);
              }}
              value={formik.values.oldPassword}
              onBlur={formik.handleBlur}
              error={formik.touched.oldPassword && formik.errors.oldPassword}
              password
            />
            {!success ? (
              <div>
                {success === null ? null : <div className={styles.redText}>{gettext('This is not your password')}</div>}
              </div>
            ) : null}
            <EditInput
              label={gettext('New Password')}
              id="newPassword"
              name="newPassword"
              testId="new-password-input"
              type="password"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={async (e) => {
                formik.handleBlur(e);
                await checkPassword(formik.values.newPassword);
              }}
              error={formik.touched.newPassword && formik.errors.newPassword}
              password
            />
            <EditInput
              label={gettext('Confirm New Password')}
              id="confirmPassword"
              name="confirmPassword"
              testId="confirm-password-input"
              type="password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && formik.errors.confirmPassword}
              password
            />
            {props.strongPasswordPolicy && (
              <div className={styles.paragraph}>
                <div className={styles.paragraphLine}>
                  <span className={styles.dot}>•</span>
                  <span>
                    {gettext('Use at least 8 characters, a combination of numbers, letters, and special characters.')}
                  </span>
                </div>
                <div className={styles.paragraphLine}>
                  <span className={styles.dot}>•</span>
                  <span>{gettext('Do not use your previous passwords.')}</span>
                </div>
                <div className={styles.paragraphLine}>
                  <span className={styles.dot}>•</span>
                  <span>
                    {gettext(
                      'Do not use your name, email address, mobile number, or any other information that might be accessed easily.',
                    )}
                  </span>
                </div>
                <div className={styles.paragraphLine}>
                  <span className={styles.dot}>•</span>
                  <span>{gettext('Do not use the same password for more than one online account.')}</span>
                </div>
              </div>
            )}
            <div className={styles.button}>
              <AnalyticsHandler eventName={AnalyticsEventNames.CHANGE_PASSWORD}>
                <Button
                  buttonClass="primary-blue"
                  type="submit"
                  text={gettext('Change Password')}
                  width="30%"
                  isLoading={props.isLoading}
                />
              </AnalyticsHandler>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.secondaryButtonWrapper}>
          <Button buttonClass="secondary-blue" text="Change Password" onClick={() => setEditMode(true)} />
        </div>
      )}
    </div>
  );
};

ChangePassword.propTypes = {
  changePassword: PropTypes.func,
  passwordUpdateStatus: PropTypes.bool,
  isLoading: PropTypes.bool,
  strongPasswordPolicy: PropTypes.bool,
  tracking: PropTypes.object,
  user: PropTypes.object,
};

export default ChangePassword;
